// src/components/deal-detail/AcceptanceEmailsPanel.jsx
import { useEffect, useMemo, useState } from "react";
import { Mail } from "lucide-react";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import {
  ACCEPTANCE_TEMPLATE_IDS,
  ACCEPTANCE_TEMPLATE_META,
} from "../../utils/acceptanceEmails";
import {
  buildContext,
  missingFieldsFor,
} from "../../utils/acceptanceEmailComposer";
import { getContactsByIds, updateItem } from "../../utils/firebasefunctions";
import { normalizeContact } from "../../utils/contactNormalize";
import AcceptanceEmailRow from "./AcceptanceEmailRow";
import SendAcceptanceEmailModal from "../email/SendAcceptanceEmailModal";

const TEMPLATES_FOR_DEAL = (dealType) => {
  const isBuyer = dealType === "Buyer";
  return ACCEPTANCE_TEMPLATE_IDS.filter((id) => {
    if (id === "seller-client-accepted") return !isBuyer;
    if (id === "buyer-client-accepted") return isBuyer;
    return true; // title-new-transaction
  });
};

export default function AcceptanceEmailsPanel({ deal, onDealUpdate }) {
  const [{ user, teamDefaults }] = useStateValue();
  const [contacts, setContacts] = useState([]);
  const [savingExtras, setSavingExtras] = useState(false);
  const [openModalFor, setOpenModalFor] = useState(null); // { templateId, mode }

  const visible = deal && deal.dealStage === "Under Contract";

  useEffect(() => {
    let cancelled = false;
    async function loadContacts() {
      const ids = Array.isArray(deal?.customers) ? deal.customers.filter(Boolean) : [];
      if (ids.length === 0) { setContacts([]); return; }
      try {
        const raws = await getContactsByIds(ids);
        const normd = (raws || []).map((c) => normalizeContact(c));
        if (!cancelled) setContacts(normd);
      } catch (err) {
        console.error("AcceptanceEmailsPanel: contact load failed:", err);
        if (!cancelled) setContacts([]);
      }
    }
    if (visible) loadContacts();
    return () => { cancelled = true; };
  }, [deal?.id, deal?.customers, visible]);

  const ctx = useMemo(
    () => buildContext(deal, contacts, teamDefaults),
    [deal, contacts, teamDefaults],
  );

  const templateIds = useMemo(
    () => TEMPLATES_FOR_DEAL(deal?.dealtype),
    [deal?.dealtype],
  );

  if (!visible) return null;

  const sentMap = deal?.acceptanceEmails?.sent || {};
  const extras = deal?.acceptanceEmails || {};

  const recipientFor = (templateId) => {
    if (templateId === "title-new-transaction") {
      return {
        name: teamDefaults?.titleCloserName || "",
        email: teamDefaults?.titleCloserEmail || "",
      };
    }
    // Client templates — first contact with an email
    const c = contacts.find((c) => c?.email);
    return {
      name: c?.name || c?.firstName || "",
      email: c?.email || "",
    };
  };

  const onSaveExtras = async (patch) => {
    setSavingExtras(true);
    try {
      const merged = { ...(deal.acceptanceEmails || {}), ...patch };
      await updateItem("deals", deal.id, { acceptanceEmails: merged });
      onDealUpdate?.({ ...deal, acceptanceEmails: merged });
      toast.success("Details Saved");
    } catch (err) {
      console.error("Save extras failed:", err);
      toast.error("Save failed. Try again.");
    } finally {
      setSavingExtras(false);
    }
  };

  const onSent = async (templateId, sentRecord) => {
    const nextSent = { ...(deal.acceptanceEmails?.sent || {}), [templateId]: sentRecord };
    const merged = { ...(deal.acceptanceEmails || {}), sent: nextSent };
    try {
      await updateItem("deals", deal.id, { acceptanceEmails: merged });
      onDealUpdate?.({ ...deal, acceptanceEmails: merged });
    } catch (err) {
      console.error("Persist sent record failed:", err);
      toast.error("Sent, but couldn't record it on the deal.");
    }
  };

  return (
    <div className="bg-white rounded-xl shadow p-6">
      <div className="border-b border-black/10 pb-3 flex items-center gap-2">
        <Mail size={18} className="text-indigo-600" />
        <h3 className="text-lg font-semibold">Send Acceptance Emails</h3>
      </div>

      <div className="space-y-2 mt-3">
        {templateIds.map((id) => {
          const recipient = recipientFor(id);
          const missing = missingFieldsFor(id, ctx);
          return (
            <AcceptanceEmailRow
              key={id}
              templateId={id}
              meta={ACCEPTANCE_TEMPLATE_META[id]}
              recipientName={recipient.name}
              recipientEmail={recipient.email}
              sentRecord={sentMap[id]}
              missingFields={missing}
              currentExtras={extras}
              savingExtras={savingExtras}
              onSaveExtras={onSaveExtras}
              onOpenSend={(mode) => setOpenModalFor({ templateId: id, mode })}
            />
          );
        })}
      </div>

      {openModalFor && (
        <SendAcceptanceEmailModal
          deal={deal}
          contacts={contacts}
          sender={user}
          settingsDefaults={teamDefaults}
          templateId={openModalFor.templateId}
          recipientName={recipientFor(openModalFor.templateId).name}
          recipientEmail={recipientFor(openModalFor.templateId).email}
          mode={openModalFor.mode}
          onClose={() => setOpenModalFor(null)}
          onSent={(record) => {
            onSent(openModalFor.templateId, record);
            setOpenModalFor(null);
          }}
        />
      )}
    </div>
  );
}
